import { HttpClient, HttpParams } from '@angular/common/http';

import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, PaginatedApiResponse } from '@card-software/library';
import { Roll } from '../../models/production/roll';
import { RollQualityControlRequest } from '../../models/production/roll-quality-control';
import { RollPayloadSearch } from '../../models/rollsqc/rolls-search';
import { RollSearchCommand } from '../../models/production/roll-search';

@Injectable({
  providedIn: 'root',
})
export class RollProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.productionApi + '/api/v1/roll';

  public searchRoll(
    command: RollSearchCommand,
    pageNumber = 1,
    pageSize = 50,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<Roll>>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('sortOrder', sortOrder.toString());

    if (sortField) {
      params = params.set('sortField', sortField);
    }

    return this.httpClient.post<ApiResponse<PaginatedApiResponse<Roll>>>(`${this.baseUrl}/search/paginated`, command, { params });
  }

  public getByRollNumbers(rollNumbers: string[]) {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/batch/numbers`, rollNumbers);
  }

  public getRollsByUnWeighed(rollNumber = ''): Observable<ApiResponse<Roll[]>> {
    const params = new HttpParams().set('excludeUnweighedRolls', true).set('pageNumber', 1).set('pageSize', 25);
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/search`, { rollNumber }, { params });
  }

  public getRollsWithQCLast48Hours(payload: RollPayloadSearch): Observable<ApiResponse<Roll[]>> {
    const params = new HttpParams().set('excludeUnweighedRolls', true).append('searchByDateRange', true);
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/search`, payload, { params });
  }

  public createWeightQualityControl(data: RollQualityControlRequest, newRollWeightInLbs = 0): Observable<ApiResponse<Roll[]>> {
    const params = new HttpParams().set('newRollWeightInLbs', newRollWeightInLbs.toString());
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/weight-quality-control`, data, { params });
  }

  public uploadFileForQC(idRoll: string, idQC: string, file: File): Observable<ApiResponse<Roll>> {
    const formData = new FormData();
    formData.append('files', file);

    const params = new HttpParams().set('rollId', idRoll).set('qualityControlId', idQC);

    return this.httpClient.post<ApiResponse<Roll>>(`${this.baseUrl}/upload-files`, formData, { params });
  }

  public getByRollNumber(
    rollNumber: string,
    transferUnitSearch = false,
    knittingOrderNumber = '',
    validateForQC = false,
  ): Observable<ApiResponse<Roll>> {
    const params = new HttpParams()
      .set('transferUnitSearch', transferUnitSearch.toString())
      .set('knittingOrderNumber', knittingOrderNumber)
      .set('validateForQC', validateForQC.toString());
    return this.httpClient.get<ApiResponse<Roll>>(`${this.baseUrl}/number/${encodeURIComponent(rollNumber)}`, { params });
  }

  public getByEdrNumber(edrNumber: string): Observable<ApiResponse<Roll>> {
    return this.httpClient.get<ApiResponse<Roll>>(`${this.baseUrl}/edr/${encodeURIComponent(edrNumber)}`);
  }

  public exportToExcel(command: RollSearchCommand): Observable<Blob> {
    return this.httpClient.post(`${this.baseUrl}/search/export`, command, {
      responseType: 'blob',
    });
  }

  /** Downloads the "scanned rolls not transferred" report (rolls still in the given locations). */
  public exportPendingRolls(command: { locationCodes: string[]; numbers?: string[] }): Observable<Blob> {
    return this.httpClient.post(`${this.baseUrl}/pending/export`, command, {
      responseType: 'blob',
    });
  }
}
